/**
 * admin/src/App.tsx
 * Router + Auth guard
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAdminAuth } from './hooks/useAdmin';
import { AdminLayout } from './components/layout/AdminLayout';
import { ToastContainer } from './components/ui/index';

// ── Pages (lazy sau — hiện tại import trực tiếp để scaffold) ──────────────────
import { LoginPage }         from './pages/LoginPage';
import { OverviewPage }      from './pages/OverviewPage';
import { UsersPage }         from './pages/UsersPage';
import { UserDetailPage }    from './pages/UserDetailPage';
import { StudentsPage }      from './pages/StudentsPage';
import { StudentDetailPage } from './pages/StudentDetailPage';
import { ContentPage }       from './pages/ContentPage';
import { BlogPage }          from './pages/BlogPage';
import { BlogEditorPage }    from './pages/BlogEditorPage';
import { SubscriptionsPage } from './pages/SubscriptionsPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { AnalyticsPage }     from './pages/AnalyticsPage';
import { FeedbackPage }      from './pages/FeedbackPage';
import { SettingsPage }       from './pages/SettingsPage';
import { EmailTemplatesPage } from './pages/EmailTemplatesPage';
import { BackupPage }         from './pages/BackupPage';

// ── Protected Route ────────────────────────────────────────────────────────────
function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { user, isLoading, isAdmin } = useAdminAuth();

  if (isLoading) {
    return (
      <div className="flex-center" style={{ height: '100vh' }}>
        <div style={{ color: 'var(--text-3)', fontSize: 14 }}>Đang kiểm tra quyền...</div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  if (!isAdmin) {
    return (
      <div className="login-page">
        <div className="login-card">
          <div className="login-card__logo">
            <span className="login-card__dot" />
            luyen-thi Admin
          </div>
          <div className="login-error">
            ❌ Tài khoản <strong>{user.email}</strong> không có quyền truy cập Admin.
          </div>
          <div style={{ marginTop: 16 }}>
            <a href="/api/auth/logout" className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center' }}>
              Đăng xuất
            </a>
          </div>
        </div>
      </div>
    );
  }

  return <AdminLayout>{children}</AdminLayout>;
}

// ── App Router ────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<LoginPage />} />

        {/* Admin — protected */}
        <Route path="/admin" element={<RequireAdmin><OverviewPage /></RequireAdmin>} />
        <Route path="/admin/users" element={<RequireAdmin><UsersPage /></RequireAdmin>} />
        <Route path="/admin/users/:id" element={<RequireAdmin><UserDetailPage /></RequireAdmin>} />
        <Route path="/admin/students" element={<RequireAdmin><StudentsPage /></RequireAdmin>} />
        <Route path="/admin/students/:id" element={<RequireAdmin><StudentDetailPage /></RequireAdmin>} />
        <Route path="/admin/content" element={<RequireAdmin><ContentPage /></RequireAdmin>} />
        <Route path="/admin/blog" element={<RequireAdmin><BlogPage /></RequireAdmin>} />
        <Route path="/admin/blog/new" element={<RequireAdmin><BlogEditorPage /></RequireAdmin>} />
        <Route path="/admin/blog/:slug/edit" element={<RequireAdmin><BlogEditorPage /></RequireAdmin>} />
        <Route path="/admin/subscriptions" element={<RequireAdmin><SubscriptionsPage /></RequireAdmin>} />
        <Route path="/admin/notifications" element={<RequireAdmin><NotificationsPage /></RequireAdmin>} />
        <Route path="/admin/analytics" element={<RequireAdmin><AnalyticsPage /></RequireAdmin>} />
        <Route path="/admin/feedback" element={<RequireAdmin><FeedbackPage /></RequireAdmin>} />
        <Route path="/admin/settings" element={<RequireAdmin><SettingsPage /></RequireAdmin>} />
        <Route path="/admin/email-templates" element={<RequireAdmin><EmailTemplatesPage /></RequireAdmin>} />
        <Route path="/admin/backup" element={<RequireAdmin><BackupPage /></RequireAdmin>} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>

      {/* Global Toast */}
      <ToastContainer />
    </BrowserRouter>
  );
}
